import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowLeft, CalendarClock, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { FadeIn, Reveal } from "@/components/Reveal";

export default function Workshop() {
  const { slug } = useParams();
  const [ws, setWs] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/workshops/${slug}`).then(({ data }) => setWs(data)).catch(() => setNotFound(true));
  }, [slug]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/workshop-registrations", { ...form, workshop_slug: ws.slug, workshop_title: ws.title });
      setDone(true);
      toast.success("You're registered", { description: "We'll confirm the date and venue with you." });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound)
    return (
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-28 text-center">
        <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Workshop not found</p>
        <Link to="/courses" className="font-display font-bold text-3xl mt-4 inline-block text-emerald underline">Back to catalog</Link>
      </div>
    );
  if (!ws)
    return (
      <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-28 text-center">
        <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide">Loading…</p>
      </div>
    );

  const inputCls = "w-full bg-transparent rounded-xl border border-line px-4 py-3 outline-none focus:border-emerald focus:ring-1 focus:ring-emerald transition-colors";

  return (
    <div data-testid="workshop-page">
      <section className="mesh-wash relative overflow-hidden">
        <div className="blob-decoration w-[420px] h-[420px] bg-lemonlight -top-32 -right-24" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-8 relative">
          <Link to={`/courses/${ws.linked_course}`} className="inline-flex items-center gap-2 font-sans text-xs font-bold text-graphite hover:text-emerald transition-colors">
            <ArrowLeft size={14} /> Related course
          </Link>
        </div>
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-6 md:pt-8 pb-10 md:pb-12 relative">
          <FadeIn>
            <span className="inline-flex items-center gap-2 rounded-full bg-lemon text-[#5a4300] px-4 py-1.5 font-sans text-xs font-bold mb-6">
              {ws.overline}
            </span>
          </FadeIn>
          <h1 className="font-display font-bold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-4xl text-ink">{ws.title}</h1>
          <p className="font-sans text-xl md:text-2xl text-ink/80 mt-6 max-w-2xl leading-snug font-medium">{ws.summary}</p>
        </div>
      </section>

      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 pt-12 md:pt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Fact icon={<CalendarClock size={16} />} label="When" value={ws.date_label} />
              <Fact icon={<Clock size={16} />} label="Duration" value={ws.duration} />
              <Fact icon={<MapPin size={16} />} label="Format" value={ws.format} />
            </div>

            <div className="mt-12 space-y-5 max-w-2xl">
              {ws.intro.map((p, i) => (
                <p key={i} className="text-lg md:text-xl leading-relaxed text-ink/90">{p}</p>
              ))}
            </div>

            <Reveal className="mt-14">
              <p className="font-sans text-xs font-bold text-terracotta uppercase tracking-wide">What we'll cover</p>
              <div className="mt-6 space-y-3">
                {ws.agenda.map((a, i) => (
                  <div key={i} className="flex items-start gap-5 rounded-2xl bg-surface border border-line p-5 shadow-sm">
                    <span className="shrink-0 w-9 h-9 rounded-full bg-emeraldlight text-emerald flex items-center justify-center font-display font-bold text-sm">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-lg leading-snug">{a}</p>
                  </div>
                ))}
              </div>
              <p className="font-sans text-xs font-semibold text-graphite mt-8">
                Led by {ws.instructor.name} — {ws.instructor.role}
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-3xl overflow-hidden bg-surface border border-line shadow-md">
              <div className="p-6 md:p-8 bg-emerald text-white">
                <p className="font-sans text-xs font-bold text-amber uppercase tracking-wide">Free · limited seats</p>
                <h2 className="font-display font-bold tracking-tight text-3xl mt-2">Register interest</h2>
                <p className="text-white/70 text-[15px] mt-3 leading-relaxed">No payment. We'll confirm the date and venue with you directly.</p>
              </div>
              <div className="p-6 md:p-8">
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div key="done" data-testid="workshop-success" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8">
                      <div className="mx-auto w-12 h-12 rounded-full bg-emerald text-white flex items-center justify-center mb-5"><Check size={22} /></div>
                      <h3 className="font-display font-semibold text-2xl">You're on the list</h3>
                      <p className="text-graphite mt-3 text-[15px] leading-relaxed">We'll be in touch to confirm the session details with you.</p>
                    </motion.div>
                  ) : (
                    <motion.form key="form" onSubmit={submit} className="space-y-4" initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <WField label="Your name"><input required value={form.name} onChange={upd("name")} data-testid="workshop-name" className={inputCls} placeholder="Full name" /></WField>
                      <WField label="Email"><input required type="email" value={form.email} onChange={upd("email")} data-testid="workshop-email" className={inputCls} placeholder="you@email.com" /></WField>
                      <WField label="Phone"><input required value={form.phone} onChange={upd("phone")} data-testid="workshop-phone" className={inputCls} placeholder="+91" /></WField>
                      {error && <p className="text-sm text-destructive font-sans" data-testid="workshop-error">{error}</p>}
                      <button type="submit" disabled={submitting} data-testid="workshop-submit" className="w-full rounded-full bg-terracotta text-white font-bold text-base px-8 py-4 shadow-md hover:bg-terracottahover hover:shadow-glowwarm transition duration-300 disabled:opacity-50">
                        {submitting ? "Registering…" : "Reserve my seat"}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-stone p-5">
      <div className="flex items-center gap-2 text-terracotta">{icon}<span className="font-sans text-xs font-bold uppercase tracking-wide">{label}</span></div>
      <p className="text-[15px] mt-2 leading-snug">{value}</p>
    </div>
  );
}

function WField({ label, children }) {
  return (
    <label className="block">
      <span className="font-sans text-xs font-bold text-graphite mb-2 block uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}
